import IconChevronDown from '@/assets/icons/font_awesome/chevron-down-solid.svg?react';
import { UI_PRIMITIVE__dropdown_menu } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu';
import { UI_PRIMITIVE__dropdown_menu_content } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_content';
import { UI_PRIMITIVE__dropdown_menu_item } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_item';
import { UI_PRIMITIVE__dropdown_menu_trigger } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_trigger';
import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__input } from '@/components/ui/UI_PRIMITIVE__input';
import { UI_PRIMITIVE__text_area } from '@/components/ui/UI_PRIMITIVE__text_area';
import { use_slice_user, use_slice_workspace } from '@/hooks/store/use_slices';
import { toast } from '@/hooks/use-toast';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_user_message_create } from '@/state/user_message/remote_user_message_create';
import { DTOUserSupportMessage } from '@shared/dto/+user/user_support_message/dto_user_support_message';
import { EUserSupportMessageTopic } from '@shared/dto/+user/user_support_message/EUserSupportMessageTopic';
import { CALLBACK__void } from '@shared/types/types';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  close_toast: CALLBACK__void;
}

export const FormHelpMessage: FC<Props> = ({ close_toast }) => {
  const { t } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  const { user_settings } = use_slice_user(['user_settings']);
  const { workspace_current } = use_slice_workspace(['workspace_current']);
  const { is_authorized, user_email } = use_slice_user(['is_authorized', 'user_email']);

  const [value_message, set_value_message] = useState('');
  const [value_email, set_value_email] = useState(is_authorized ? user_email : '');
  const [topic_selected, set_topic_selected] = useState(EUserSupportMessageTopic.OTHER);

  const trigger_id = 'dropdown_help_write_message';

  /*
   * EVENTS
   */

  async function on_button_submit_clicked() {
    close_toast();

    if (!value_message.trim()) return;

    set_value_message('');
    set_value_email('');
    const message_dto = new DTOUserSupportMessage();
    message_dto.workspace_id = workspace_current._id;
    message_dto.message = value_message;
    message_dto.topic = topic_selected;
    message_dto.email_from_form = value_email;
    message_dto.language_from_browser = user_settings.regional_language;

    await remote_user_message_create(message_dto);
    toast({ title: t(i18n_base_ui.toasts.message_sent.title) });
  }

  /*
   * EFFECTS
   */

  /*
   * RENDERS
   */

  function render_items() {
    return Object.values(EUserSupportMessageTopic).map(value => (
      <UI_PRIMITIVE__dropdown_menu_item
        key={value}
        label={t((i18n_base_ui.toasts.send_message.dropdown.topics as any)[value])}
        class_name_hovered_selected={`bg-accent`}
        on_item_selected={() => set_topic_selected(value)}
        {...{ value }}
        values_selected={[topic_selected]}
        className={`w-full rounded-lg cursor-pointer text-bar-foreground h-8 px-4`}
      />
    ));
  }

  /*
   * HTML
   */

  return (
    <div className="flex flex-col gap-2 cursor-default">
      <UI_PRIMITIVE__dropdown_menu>
        <UI_PRIMITIVE__dropdown_menu_trigger asChild>
          <UI_PRIMITIVE__button id={trigger_id} className="w-full relative justify-start h-9" variant="outline-on-bar">
            <div className="flex flex-row items-start justify-start max-w-48 overflow-hidden">
              <span>{t((i18n_base_ui.toasts.send_message.dropdown.topics as any)[topic_selected])}</span>
            </div>
            <div className="absolute top-2.5 right-4">
              <IconChevronDown className="!w-3 !h-3" />
            </div>
          </UI_PRIMITIVE__button>
        </UI_PRIMITIVE__dropdown_menu_trigger>
        <UI_PRIMITIVE__dropdown_menu_content trigger_id={trigger_id} className="rounded !bg-bar" align="start">
          {render_items()}
        </UI_PRIMITIVE__dropdown_menu_content>
      </UI_PRIMITIVE__dropdown_menu>
      <UI_PRIMITIVE__input
        type="email"
        placeholder={t(i18n_base_ui.toasts.send_message.input.email.placeholder)}
        value={value_email}
        onChange={e => set_value_email(e.target.value)}
        className="w-full h-10"
      />
      <UI_PRIMITIVE__text_area
        className="rounded-lg px-3 max-h-[28rem] "
        placeholder={t(i18n_base_ui.toasts.send_message.input.message.placeholder)}
        lineCount={35}
        maxLength={825}
        value={value_message}
        onChange={e => set_value_message(e.target.value)}
      />
      <UI_PRIMITIVE__button
        onClick={on_button_submit_clicked}
        disabled={!value_message.trim() || !value_email.trim()}
        variant="outline-on-component"
        className="w-full h-10"
      >
        {t(i18n_base_ui.toasts.send_message.button.send.title)}
      </UI_PRIMITIVE__button>
    </div>
  );
};
